import { useEffect, useId, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { ShotLightbox } from '@/components/site/ShotLightbox';
import {
  ArrowRight,
  Boxes,
  BookOpen,
  Users,
  Building2,
  ChevronRight,
  LayoutGrid,
  MessageSquare,
  Phone,
  Tags,
  X,
} from 'lucide-react';

const NAV = [
  { to: '/produto', label: 'Produto', hint: 'Inbox, funil e WhatsApp oficial', Icon: MessageSquare },
  { to: '/apps', label: 'Apps', hint: 'Módulos sob demanda', Icon: LayoutGrid },
  { to: '/setores', label: 'Setores', hint: 'Fluxos do seu negócio', Icon: Building2 },
  { to: '/clientes', label: 'Clientes', hint: 'Resultados reais, com dados', Icon: Users },
  { to: '/precos', label: 'Preços', hint: 'Planos claros, sem fidelidade', Icon: Tags },
  { to: '/guias', label: 'Guias', hint: 'WhatsApp oficial, sem mistério', Icon: BookOpen },
  { to: '/sobre', label: 'Sobre', hint: 'Empresa e segurança', Icon: Boxes },
] as const;

function SiteNav() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const drawerId = useId();
  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <header className={`nav${open ? ' nav-open' : ''}`}>
      <div className="shell nav-in">
        <Link to="/" aria-label="Operax, início" style={{ flexShrink: 0 }}>
          <img className="nav-logo" src="/operax-logo-nav.webp" alt="Operax" width={175} height={34} decoding="async" />
        </Link>
        <nav className="nav-links" aria-label="Principal">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => (isActive ? 'active' : '')}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="nav-cta">
          <a className="nav-login" href="https://app.operax.digital/login">
            Acessar plataforma
          </a>
          <Link className="btn btn-primary nav-cta-plans" to="/assinar">
            Assinar agora
          </Link>
          <button
            type="button"
            className="nav-burger"
            aria-label={open ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={open}
            aria-controls={drawerId}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? (
              <X strokeWidth={2.2} width={22} height={22} />
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} width={22} height={22}>
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {open ? (
        <>
          <button type="button" className="nav-scrim" aria-label="Fechar menu" onClick={() => setOpen(false)} />
          <div
            id={drawerId}
            className="nav-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Menu de navegação"
          >
            <nav className="nav-drawer-list" aria-label="Mobile">
              {NAV.map(({ to, label, hint, Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) => `nav-drawer-item${isActive ? ' active' : ''}`}
                >
                  <span className="nav-drawer-ic" aria-hidden>
                    <Icon strokeWidth={2} width={20} height={20} />
                  </span>
                  <span className="nav-drawer-copy">
                    <span className="nav-drawer-label">{label}</span>
                    <span className="nav-drawer-hint">{hint}</span>
                  </span>
                  <ChevronRight className="nav-drawer-chev" strokeWidth={2} width={18} height={18} aria-hidden />
                </NavLink>
              ))}
              <Link to="/contato" className="nav-drawer-item">
                <span className="nav-drawer-ic" aria-hidden>
                  <Phone strokeWidth={2} width={20} height={20} />
                </span>
                <span className="nav-drawer-copy">
                  <span className="nav-drawer-label">Contato</span>
                  <span className="nav-drawer-hint">Falar com a equipe</span>
                </span>
                <ChevronRight className="nav-drawer-chev" strokeWidth={2} width={18} height={18} aria-hidden />
              </Link>
            </nav>

            <div className="nav-drawer-foot">
              <Link className="btn btn-primary" to="/precos">
                Ver planos <ArrowRight width={17} height={17} aria-hidden />
              </Link>
            </div>
          </div>
        </>
      ) : null}
    </header>
  );
}

function SiteFooter() {
  return (
    <footer>
      <div className="shell">
        <div className="foot-top sitemap">
          <div className="foot-brand">
            <img className="foot-logo" src="/operax-logo-nav.webp" alt="Operax" width={144} height={28} loading="lazy" decoding="async" />
            <p>
              Atendimento, funil e apps num único acesso, pelo WhatsApp oficial da Meta. Operada pela Castor
              Consultoria, em Timóteo/MG.
            </p>
            <div
              className="badge badge-wa"
              style={{
                marginTop: 16,
                background: 'rgba(37,211,102,.1)',
                borderColor: 'rgba(37,211,102,.3)',
                color: '#8fe6b6',
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#25d366' }} />
              Provedor de Tecnologia · Meta
            </div>
          </div>
          <div className="foot-col">
            <p className="foot-h">Produto</p>
            <Link to="/produto">Plataforma</Link>
            <Link to="/apps">Portal de Apps</Link>
            <Link to="/precos">Preços</Link>
            <Link to="/produto#tour">Tour do sistema</Link>
          </div>
          <div className="foot-col">
            <p className="foot-h">Soluções</p>
            <Link to="/setores">Setores</Link>
            <Link to="/setores#clinica">Clínicas &amp; saúde</Link>
            <Link to="/setores#imobiliaria">Imobiliárias</Link>
            <Link to="/setores#autopecas">Autopeças</Link>
          </div>
          <div className="foot-col">
            <p className="foot-h">Empresa</p>
            <Link to="/sobre">Sobre</Link>
            <Link to="/clientes">Clientes</Link>
            <Link to="/casos/protvaco">Caso Protvaço</Link>
            <Link to="/casos/consultorio-dr-lucas">Caso consultório Dr. Lucas</Link>
            <Link to="/casos/metodo-stop-gastos">Caso Método STOP Gastos</Link>
            <Link to="/casos/supermercado-somar">Caso Supermercado Somar</Link>
            <Link to="/contato">Contato</Link>
            <Link to="/sobre#seguranca">Segurança &amp; LGPD</Link>
          </div>
          <div className="foot-col">
            <p className="foot-h">Legal</p>
            <Link to="/politica-de-privacidade">Política de privacidade</Link>
            <Link to="/termos-de-uso">Termos de uso</Link>
            <Link to="/exclusao-de-dados">Exclusão de dados</Link>
          </div>
        </div>
        <div className="foot-bottom">
          <span>Operax é operado por CASTOR CONSULTORIA E DESENVOLVIMENTO LTDA - ME, CNPJ 41.769.835/0001-98.</span>
          <span>© 2026 Operax · Timóteo/MG</span>
        </div>
      </div>
    </footer>
  );
}

export function InstitutionalLayout() {
  const { pathname, hash } = useLocation();

  // Reveal-on-scroll: observa .rev na rota atual E em nós injetados depois
  // (ex.: cards de plano/apps que só montam após o fetch da API). Sem MutationObserver
  // esses elementos ficavam com opacity:0 para sempre.
  useEffect(() => {
    const root = document.querySelector('.opx-site');
    if (!root) return;

    const revealNow = (el: Element) => el.classList.add('in');

    if (!('IntersectionObserver' in window)) {
      root.querySelectorAll('.rev').forEach(revealNow);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -4% 0px' },
    );

    const watched = new WeakSet<Element>();
    const watch = (els: Iterable<Element>) => {
      for (const el of els) {
        if (!(el instanceof HTMLElement) || el.classList.contains('in') || watched.has(el)) continue;
        watched.add(el);
        io.observe(el);
      }
    };

    watch(root.querySelectorAll('.rev'));

    const mo = new MutationObserver(() => {
      watch(root.querySelectorAll('.rev:not(.in)'));
    });
    mo.observe(root, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      io.disconnect();
    };
  }, [pathname]);

  // Scroll ao topo (ou até a âncora) a cada navegação.
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return (
    <div className="opx-site">
      <SiteNav />
      <main>
        <Outlet />
      </main>
      <SiteFooter />
      <ShotLightbox />
    </div>
  );
}
